// Renderiza un módulo según su `kind`. Cada tipo tiene su layout específico.
// El usuario puede eliminarlo o editarlo (editor JSON inline).

import React, { useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaTrashAlt,
  FaEdit,
  FaCheck,
  FaTimes,
  FaUserCircle,
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaLightbulb,
  FaIndustry,
  FaExternalLinkAlt,
  FaProjectDiagram,
  FaListUl,
  FaChartBar,
  FaQuoteRight,
  FaClock,
  FaTable,
  FaNewspaper,
  FaPenFancy,
} from 'react-icons/fa';
import {
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

const Card = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $kind }) =>
    KIND_COLOR[$kind] || 'rgba(0, 180, 216, 0.4)'};
  border-radius: 10px;
  padding: 0.95rem 1.05rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  position: relative;

  .head {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    flex-wrap: wrap;
  }
  .icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.3);
    color: ${accent};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .title {
    flex: 1;
    min-width: 0;
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.95rem;
  }
  .badge {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    padding: 2px 6px;
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-radius: 4px;
  }
  .actions {
    display: flex;
    gap: 4px;
    opacity: 0.6;
    transition: opacity 0.18s ease;
  }
  &:hover .actions { opacity: 1; }
  .actions button {
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px;
    height: 26px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.72rem;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover { color: ${accent}; }
    &.danger:hover { color: var(--alert-error); border-color: rgba(239,71,111,0.4); }
  }

  .md {
    color: var(--text-secondary);
    font-size: 0.88rem;
    line-height: 1.6;
    h1, h2, h3, h4 { color: var(--text-primary); margin: 0.6rem 0 0.35rem; font-size: 0.96rem; }
    p { margin: 0 0 0.5rem; }
    p:last-child { margin: 0; }
    ul, ol { margin: 0.3rem 0 0.5rem 1.2rem; padding: 0; }
    li { margin-bottom: 0.2rem; }
    table { border-collapse: collapse; font-size: 0.84rem; margin: 0.5rem 0; }
    th, td { border: 1px solid ${borderSoft}; padding: 4px 8px; }
    th { background: rgba(0, 180, 216, 0.06); color: var(--text-primary); }
    code { background: rgba(0,180,216,0.1); color: ${accent}; padding: 1px 5px; border-radius: 3px; }
    a { color: ${accent}; }
  }

  .item-row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.5rem 0.65rem;
    background: rgba(11, 19, 32, 0.45);
    border: 1px solid ${borderSoft};
    border-radius: 6px;
    margin-bottom: 0.4rem;
    font-size: 0.86rem;
    color: var(--text-secondary);

    .marker { color: ${accent}; flex-shrink: 0; padding-top: 2px; }
    .body { flex: 1; min-width: 0; }
    .meta {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
      margin-top: 3px;
    }
    a { color: ${accent}; }
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.6rem;
  }
  .stat-cell {
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    padding: 0.6rem 0.75rem;

    .label {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    .value {
      font-size: 1.5rem;
      color: ${accent};
      font-weight: 700;
      margin: 3px 0;
    }
    .sub { color: var(--text-secondary); font-size: 0.78rem; }
  }

  .blockquote {
    border-left: 3px solid ${accent};
    background: rgba(0, 180, 216, 0.05);
    padding: 0.7rem 0.9rem;
    color: var(--text-primary);
    font-style: italic;
    border-radius: 5px;
    .source {
      display: block;
      margin-top: 6px;
      font-style: normal;
      font-family: ${mono};
      font-size: 0.62rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
  }

  table.cmp {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.84rem;
    margin-top: 4px;

    th, td {
      border: 1px solid ${borderSoft};
      padding: 6px 10px;
      text-align: left;
      color: var(--text-secondary);
    }
    th {
      background: rgba(0, 180, 216, 0.06);
      color: var(--text-primary);
      font-family: ${mono};
      font-size: 0.66rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
  }

  textarea.json-edit {
    width: 100%;
    min-height: 200px;
    background: rgba(11, 19, 32, 0.85);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    font-family: ${mono};
    font-size: 0.78rem;
    padding: 0.55rem 0.75rem;
    outline: none;
    resize: vertical;
    &:focus { border-color: rgba(0, 180, 216, 0.5); }
  }
`;

const KIND_LABEL = {
  note: 'Nota',
  references: 'Referencias',
  connections: 'Conexiones',
  entity_list: 'Entidades',
  timeline: 'Timeline',
  stats: 'Stats',
  comparison: 'Comparativa',
  quote: 'Cita',
  news_feed: 'Feed',
  custom: 'Custom',
};

const KIND_COLOR = {
  note: 'rgba(0, 180, 216, 0.5)',
  references: 'rgba(6, 214, 160, 0.5)',
  connections: 'rgba(239, 71, 111, 0.5)',
  entity_list: 'rgba(167, 139, 250, 0.5)',
  timeline: 'rgba(244, 162, 97, 0.5)',
  stats: 'rgba(251, 191, 36, 0.5)',
  comparison: 'rgba(0, 180, 216, 0.5)',
  quote: 'rgba(156, 163, 175, 0.5)',
  news_feed: 'rgba(6, 214, 160, 0.5)',
  custom: 'rgba(0, 180, 216, 0.4)',
};

function KindIcon({ kind }) {
  if (kind === 'note') return <FaPenFancy />;
  if (kind === 'references') return <FaListUl />;
  if (kind === 'connections') return <FaProjectDiagram />;
  if (kind === 'entity_list') return <FaUserCircle />;
  if (kind === 'timeline') return <FaClock />;
  if (kind === 'stats') return <FaChartBar />;
  if (kind === 'comparison') return <FaTable />;
  if (kind === 'quote') return <FaQuoteRight />;
  if (kind === 'news_feed') return <FaNewspaper />;
  return <FaPenFancy />;
}

function EntityRow({ item }) {
  const Icon =
    item.type === 'person' ? FaUserCircle :
    item.type === 'company' ? FaIndustry :
    item.type === 'organization' ? FaBuilding :
    item.type === 'place' ? FaMapMarkerAlt :
    item.type === 'event' ? FaCalendarAlt :
    FaLightbulb;
  return (
    <div className="item-row">
      <span className="marker"><Icon /></span>
      <div className="body">
        <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{item.name}</div>
        {item.role && <div style={{ fontSize: '0.82rem' }}>{item.role}</div>}
        {item.summary && <div style={{ fontSize: '0.8rem', marginTop: 3 }}>{item.summary}</div>}
        {item.type && <div className="meta">{item.type}</div>}
      </div>
    </div>
  );
}

function renderBody(module) {
  const data = module.data || {};

  if (module.kind === 'note' || module.kind === 'custom') {
    return (
      <div className="md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content || '_(vacío)_'}</ReactMarkdown>
      </div>
    );
  }

  if (module.kind === 'references') {
    const items = Array.isArray(data.items) ? data.items : [];
    if (items.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin referencias.</div>;
    return (
      <div>
        {items.map((r, i) => (
          <div key={i} className="item-row">
            <span className="marker">[{r.idx ?? i + 1}]</span>
            <div className="body">
              {r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.titulo || r.url}</a> : <span>{r.titulo}</span>}
              <div className="meta">{r.fuente || ''}{r.fecha ? ' · ' + r.fecha : ''}</div>
            </div>
            {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={{ color: accent }}><FaExternalLinkAlt size={11} /></a>}
          </div>
        ))}
      </div>
    );
  }

  if (module.kind === 'connections') {
    const items = Array.isArray(data.items) ? data.items : [];
    if (items.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin conexiones.</div>;
    return (
      <div>
        {items.map((c, i) => (
          <div key={i} className="item-row">
            <span className="marker">→</span>
            <div className="body">
              <div style={{ color: 'var(--text-primary)' }}>
                <strong>{c.from}</strong> → <strong>{c.to}</strong>
              </div>
              {c.label && <div style={{ fontSize: '0.82rem', marginTop: 2 }}>{c.label}</div>}
              <div className="meta">{c.kind}{c.strength != null ? ` · strength ${c.strength}` : ''}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (module.kind === 'entity_list') {
    const items = Array.isArray(data.items) ? data.items : [];
    if (items.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin entidades.</div>;
    return <div>{items.map((it, i) => <EntityRow key={i} item={it} />)}</div>;
  }

  if (module.kind === 'timeline') {
    const items = Array.isArray(data.items) ? data.items : [];
    if (items.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin eventos.</div>;
    return (
      <div>
        {items.map((e, i) => (
          <div key={i} className="item-row">
            <span className="marker"><FaClock /></span>
            <div className="body">
              <div className="meta" style={{ marginBottom: 3 }}>{e.date || 's/f'}</div>
              <div>{e.event}</div>
              {Array.isArray(e.evidence_idx) && e.evidence_idx.length > 0 && (
                <div className="meta">Evidencia: {e.evidence_idx.join(', ')}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (module.kind === 'stats') {
    const items = Array.isArray(data.items) ? data.items : [];
    return (
      <div className="stat-grid">
        {items.map((s, i) => (
          <div key={i} className="stat-cell">
            <div className="label">{s.label}</div>
            <div className="value">{s.value}</div>
            {s.sub && <div className="sub">{s.sub}</div>}
          </div>
        ))}
      </div>
    );
  }

  if (module.kind === 'comparison') {
    const headers = Array.isArray(data.headers) ? data.headers : [];
    const rows = Array.isArray(data.rows) ? data.rows : [];
    if (headers.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin tabla.</div>;
    return (
      <table className="cmp">
        <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    );
  }

  if (module.kind === 'quote') {
    if (!data.text) return <div style={{ color: 'var(--text-disabled)' }}>Sin cita.</div>;
    return (
      <div className="blockquote">
        "{data.text}"
        {(data.source || data.url) && (
          <span className="source">
            — {data.source || 'fuente sin nombre'}
            {data.url && <> · <a href={data.url} target="_blank" rel="noreferrer" style={{ color: accent }}>ver</a></>}
          </span>
        )}
      </div>
    );
  }

  if (module.kind === 'news_feed') {
    const items = Array.isArray(data.items) ? data.items : [];
    if (items.length === 0) return <div style={{ color: 'var(--text-disabled)' }}>Sin noticias.</div>;
    return (
      <div>
        {items.map((n, i) => (
          <div key={i} className="item-row">
            <span className="marker"><FaNewspaper /></span>
            <div className="body">
              {n.url ? <a href={n.url} target="_blank" rel="noreferrer">{n.titulo}</a> : <span>{n.titulo}</span>}
              <div className="meta">{n.fuente || ''}{n.fecha ? ' · ' + n.fecha : ''}</div>
              {n.summary && <div style={{ marginTop: 4 }}>{n.summary}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <pre style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{JSON.stringify(data, null, 2)}</pre>;
}

export default function AnalysisModule({ module, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState(module.title || '');
  const [err, setErr] = useState(null);

  const startEdit = () => {
    setDraft(JSON.stringify(module.data || {}, null, 2));
    setTitleDraft(module.title || '');
    setErr(null);
    setEditing(true);
  };

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(draft); } catch (e) { setErr('JSON inválido: ' + e.message); return; }
    try {
      await onUpdate({ title: titleDraft || null, data: parsed });
      setEditing(false);
    } catch (e) {
      setErr(friendlyError(e));
    }
  };

  return (
    <Card $kind={module.kind}>
      <div className="head">
        <span className="icon"><KindIcon kind={module.kind} /></span>
        <span className="title">{module.title || KIND_LABEL[module.kind] || module.kind}</span>
        <span className="badge">{KIND_LABEL[module.kind] || module.kind}</span>
        {module.generated_by?.startsWith('agent') && <span className="badge">IA</span>}
        <div className="actions">
          {editing ? (
            <>
              <button type="button" onClick={save} title="Guardar"><FaCheck /></button>
              <button type="button" onClick={() => setEditing(false)} title="Cancelar"><FaTimes /></button>
            </>
          ) : (
            <>
              <button type="button" onClick={startEdit} title="Editar JSON"><FaEdit /></button>
              <button type="button" className="danger" onClick={() => { if (window.confirm('¿Eliminar módulo?')) onDelete(); }} title="Eliminar"><FaTrashAlt /></button>
            </>
          )}
        </div>
      </div>
      {editing ? (
        <>
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            placeholder="Título del módulo"
            style={{
              background: 'rgba(11, 19, 32, 0.65)',
              border: '1px solid ' + borderSoft,
              borderRadius: 6,
              color: 'var(--text-primary)',
              padding: '6px 10px',
              fontSize: '0.85rem',
              outline: 'none',
            }}
          />
          <textarea
            className="json-edit"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {err && <div style={{ color: 'var(--alert-error)', fontSize: '0.82rem' }}>{err}</div>}
        </>
      ) : (
        renderBody(module)
      )}
    </Card>
  );
}

export { KIND_LABEL, KIND_COLOR };
